<template>
  <div class="client-contact-form">
    <form class="vertical">
      <section v-if="canViewUserAccount">
        <h1>{{ $t('clientContact.labels.userAccount') }}</h1>
        <div>
          <router-link :to="`/users/${formData.user.reference_number}`"
            ><font-awesome-icon icon="user"></font-awesome-icon>
            {{ formData.user.username }}</router-link
          >
          <span v-if="!formData.user.is_active" class="inactive">
            {{ $t('users.labels.inactiveUser') }}
          </span>
        </div>
      </section>

      <section>
        <div class="form-control">
          <label>{{ $t('clientContact.labels.firstName') }}<span class="asterisk">*</span></label>
          <InputField
            v-model="formData.user.first_name"
            type="text"
            class="form-input"
            :class="{ error: formErrors['First Name'] }"
            @change="onChange"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('clientContact.labels.lastName') }}<span class="asterisk">*</span></label>
          <InputField
            v-model="formData.user.last_name"
            type="text"
            class="form-input"
            :class="{ error: formErrors['Last Name'] }"
            @change="onChange"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('clientContact.labels.preferredName') }}</label>
          <InputField
            v-model="formData.user.preferred_name"
            type="text"
            class="form-input"
            @change="onChange"
          />
        </div>
        <div class="form-control" :class="{ error: formErrors['Client'] }">
          <label>{{ $t('clientContact.labels.client') }}<span class="asterisk">*</span></label>
          {{ formData.clientName }}
          <TolTypeAhead
            v-show="!formData.clientName"
            :search-params="{
              url: '/clients/client-quick-search',
              searchKeys: ['name']
            }"
            class="blue contact-box"
            :menu-label="'Client Name'"
            @select-item="onClientSelect($event)"
          >
            <template v-slot="{ value }">
              {{ value.name }}
            </template>
          </TolTypeAhead>
          <tol-list :list-items="formData.clients">
            <template v-slot:body="{ value }">
              <div class="client-list-body">
                <span @click="routeToClient(value)">{{ value.name }}</span>
                <span class="list-action" @click="removeOnScreenValue(value.id)">
                  <font-awesome-icon icon="minus-circle"></font-awesome-icon>
                </span>
              </div>
            </template>
          </tol-list>
        </div>
        <div class="form-control">
          <label>{{ $t('clientContact.labels.userAccount') }}</label>
          <label> {{ formData.user.username }} </label>
        </div>

        <div class="break" />

        <div class="form-control">
          <label>{{ $t('clientContact.labels.emails') }}<span class="asterisk">*</span></label>
          <InputField
            v-model="formData.user.email"
            type="text"
            class="form-input"
            field-icon="envelope"
            :class="{ error: formErrors['Email'] }"
            @change="onChange"
          />
          <InputField
            v-model="formData.user.email_alt"
            type="text"
            class="form-input"
            field-icon="envelope"
            @change="onChange"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('clientContact.labels.phoneNumbers') }}</label>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone1"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone1_type"
              class="blue"
              @select-item="onPhone1TypeChange"
            />
          </div>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone2"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone2_type"
              class="blue"
              @select-item="onPhone2TypeChange"
            />
          </div>
          <div class="multi-inline">
            <InputField
              v-model="formData.user.phone3"
              type="text"
              class="form-input"
              field-icon="phone-square"
              @change="onChange"
            />
            <DropdownSelect
              :menu-items="phoneTypeOptions"
              :selected-item="formData.user.phone3_type"
              class="blue"
              @select-item="onPhone1TypeChange"
            />
          </div>
        </div>
        <div class="form-control address">
          <label>{{ $t('clientContact.labels.address') }}</label>
          <div>
            <label>{{ $t('clientContact.labels.line1') }}</label>
            <InputField
              v-model="formData.address.line1"
              type="text"
              class="form-input"
              @change="onChange"
            />
            <label>{{ $t('clientContact.labels.line2') }}</label>
            <InputField
              v-model="formData.address.line2"
              type="text"
              class="form-input"
              @change="onChange"
            />
            <label>{{ $t('clientContact.labels.city') }}</label>
            <InputField
              v-model="formData.address.city"
              type="text"
              class="form-input"
              @change="onChange"
            />
            <label>{{ $t('clientContact.labels.province') }}</label>
            <DropdownSelect
              :menu-items="provinceAbbrs"
              :selected-item="formData.address.province_abbr"
              class="blue province-abbrs"
              @select-item="onProvinceChange"
            />
            <label>{{ $t('clientContact.labels.postalCode') }}</label>
            <InputField
              v-model="formData.address.postal_code"
              type="text"
              class="form-input"
              @change="onChange"
            />
          </div>
        </div>

        <div class="break" />

        <div class="form-control">
          <label>{{ $t('clientContact.labels.active') }}</label>
          <checkbox
            size="tiny"
            :checked="formData.user.is_active"
            @change="updateProfileStatus()"
          />
        </div>
        <div class="module-action-container">
          <tol-button
            id="save-btn"
            button-type="save"
            label="Save"
            :class="['module-action', { disabled: !isOnScreenDataModified }]"
            @click="doClientContactSave()"
          >
          </tol-button>
        </div>
      </section>
    </form>
    <update-modal v-if="isProcessBtnClicked">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
  </div>
</template>
<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import { AppGetters } from '@/app/app.keys';
import {
  ClientContactGetters,
  ClientContactActions
} from '@/app/features/client-contacts/features/client-contact-details/client-contact-details.keys';
import TolButton from '@/app/components/TolButton';
import Checkbox from '@/app/components/Checkbox';
import * as R from 'ramda';
import TolList from '@/app/components/TolList';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';
import UpdateModal from '@/app/components/UpdateModal';
export default {
  components: {
    InputField,
    TolButton,
    UpdateModal,
    DropdownSelect,
    Checkbox,
    TolList,
    TolTypeAhead
  },
  props: {
    clientContact: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {},
      isOnScreenDataModified: false,
      isProcessBtnClicked: false
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    canViewUserAccount() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['USER_ACCOUNT'] &&
        !!this.currentUser.permissions['USER_ACCOUNT']['VIEW']
      );
    },
    getClient() {
      return this.clientContact.clients.map(c => c.name);
    },
    phoneTypeOptions() {
      return this.$store.getters[AppGetters.PhoneTypes];
    },
    provinceAbbrs() {
      return this.$store.getters[AppGetters.ProvinceAbbreviations];
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },
    formErrors() {
      return this.$store.getters[ClientContactGetters.GetClientContactValidationErrors];
    }
  },
  watch: {
    clientContact: {
      immediate: true,
      handler() {
        this.formData = R.clone(this.clientContact);
        if (!this.formData.address) {
          this.formData.address = {
            line1: null,
            line2: null,
            city: null,
            postal_code: null,
            province_abbr: null
          };
        }
      }
    }
  },
  methods: {
    onChange() {
      this.isOnScreenDataModified = true;
    },
    onProvinceChange(event) {
      this.formData.address.province_abbr = event;
      this.isOnScreenDataModified = true;
    },
    onPhone1TypeChange(event) {
      this.formData.user.phone1_type = event;
      this.isOnScreenDataModified = true;
    },
    onPhone2TypeChange(event) {
      this.formData.user.phone2_type = event;
      this.isOnScreenDataModified = true;
    },
    onPhone3TypeChange(event) {
      this.formData.user.phone3_type = event;
      this.isOnScreenDataModified = true;
    },
    updateProfileStatus() {
      this.isOnScreenDataModified = true;
      this.formData.user.is_active = !this.formData.user.is_active;
    },
    removeOnScreenValue(event) {
      this.isOnScreenDataModified = true;
      this.formData.clients = this.formData.clients.filter(element => element.id !== event);
    },
    onClientSelect(event) {
      this.isOnScreenDataModified = true;
      this.formData.clients.push({ id: event.id, name: event.name });
    },
    routeToClient(event) {
      this.$router.push(`/clients/${event.reference_number}`);
    },
    doClientContactSave() {
      if (this.formData.clientName) {
        this.formData.clients[0] = { id: this.formData.clientId, name: this.formData.clientName };
      }
      this.isProcessBtnClicked = true;
      this.$store.dispatch(ClientContactActions.SaveClientContact, this.formData);
      window.setTimeout(() => {
        this.isProcessBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/app/styles/colors.scss';
@import '@/app/styles/action-buttons.scss';

.client-contact-form {
  form {
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      a {
        text-decoration: none;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .inactive {
        color: $red;
        border: 1px solid $red;
        padding: 0 5px;
        margin-left: 5px;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 30%;
          }
        }

        &.address {
          > label {
            width: 50%;
            margin-left: 50%;
          }

          > div {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;

            > label {
              text-align: right;
            }

            > * {
              flex: 50%;
              padding-right: 1em;
            }

            .province-abbrs {
              width: 4em;
            }
          }
        }
        &.error {
          background-color: $modified-form;
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
    }

    .module-action-container {
      justify-content: flex-end;
    }

    .list-action {
      padding-left: 5px;
    }
  }
}
.asterisk {
  color: red;
}
</style>
